import { z } from "zod";

export const MAX_DOCUMENT_REFERENCES = 20;
export const MAX_DOCUMENT_FILE_NAME_LENGTH = 500;
export const MAX_DOCUMENT_CATEGORY_LABEL_LENGTH = 200;

/**
 * One financial document attached to a task-chat message as a reference
 * (distinct from message attachments, which are files uploaded directly to
 * the message). Used for "ask about this document" flows from the Upload
 * Statements (document retrieval) and Upload Documents task bodies.
 *
 * Snapshot fields are CLIENT-SUPPLIED AND UNVERIFIED — provided by the
 * browser at send time and NOT validated against server-side document data.
 * Never use them for business logic or financial computations. Treat as a
 * display-only hint that may be stale or fabricated.
 */
export const DocumentReferenceSchema = z.object({
    /** Task the document list belongs to */
    taskId: z.string().uuid(),
    documentId: z.number().int().positive(),
    /** Filename snapshot captured at send time */
    fileName: z.string().min(1).max(MAX_DOCUMENT_FILE_NAME_LENGTH),
    /** Document category label snapshot captured at send time */
    documentCategoryLabel: z
        .string()
        .max(MAX_DOCUMENT_CATEGORY_LABEL_LENGTH)
        .nullable()
        .optional(),
    /**
     * Optional human-readable chip label, e.g. "Statement - March 2025.pdf".
     * Generated client-side for display; not required for persistence.
     */
    label: z.string().max(500).optional(),
});
export type DocumentReference = z.infer<typeof DocumentReferenceSchema>;

export function buildDocumentRefLabel(
    ref: Pick<DocumentReference, "fileName">,
): string {
    return ref.fileName;
}
